import axios from "axios";
import { ETaskStatus, ITask } from "../data_models/task";
import { TASK_ENDPOINTS } from "../config/apiEndpoints";
import useAuth from "./useAuth";
import { IProject } from "../data_models/project";
import { EmployerWallet } from "../data_models/employerWallet";
import { IFreelancer } from "../data_models/freelancer";

function useTasks() {

    const { authToken } = useAuth();

    const createTask = async (projectId: string, task: ITask): Promise<{task: ITask, project: IProject}> => {
        try {
            const response = await axios.post(`${TASK_ENDPOINTS.POST_TASK}`,
                {
                    projectId,
                    task
                },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });

            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    /*
    Props options
    {
        maxAmountOfTalent: number
    }
    */
    const generateTasksAi = async (projectId: string, props: any) => {
        try {
            const response = await axios.post(`${TASK_ENDPOINTS.POST_TASKS_AI_GENERATE_MULTIPLE}`,
                { projectId, props },
                { headers: { Authorization: `${authToken}` } });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchTaskById = async (id: string) => {
        try {
            const response = await axios.get(`${TASK_ENDPOINTS.GET_TASK_BY_ID}/${id}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchTasksByIds = async (ids: string[]) => {
        try {
            const response = await axios.post(`${TASK_ENDPOINTS.POST_MULTIPLE_TASKS_BY_IDS}`,
                { ids },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchTasksByFilter = async (filter: any, page: number, limit: number) => {
        try {
            const response = await axios.post(`${TASK_ENDPOINTS.POST_TASKS_BY_FILTER}`,
                {
                    filter: filter,
                    page: page,
                    limit: limit
                },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchTasksForPmByFilter = async (projectManagerId: string, page: number, limit: number, filter?: any) => {
        try {
            const response = await axios.post(`${TASK_ENDPOINTS.POST_TASKS_FOR_PM_BY_FILTER}`,
                {
                    projectManagerId: projectManagerId,
                    filter: filter,
                    page: page,
                    limit: limit
                },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchTasksByProjectId = async (projectId: string) => {
        try {
            const response = await axios.get(`${TASK_ENDPOINTS.GET_TASKS_BY_PROJECT_ID}/${projectId}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchAllAvailableTasksAsFreelancer = async (page: number, limit: number): Promise<ITask[]> => {
        try {
            const response = await axios.get(`${TASK_ENDPOINTS.GET_AVAILABLE_TASKS_ALL}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
                params: {
                    page: page,
                    limit: limit
                }
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchAvailableTasksAsFreelancerByFilter = async (page: number, limit: number, filter: any): Promise<ITask[]> => {
        try {
            const response = await axios.post(`${TASK_ENDPOINTS.GET_AVAILABLE_TASKS_BY_FILTER}`,
                {
                    filter: filter,
                    page: page,
                    limit: limit
                },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchAllUnassignedTasks = async (page: number, limit: number) => {
        try {
            const response = await axios.get(`${TASK_ENDPOINTS.GET_UNASSIGNED_TASKS}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
                params: {
                    page: page,
                    limit: limit
                }
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const fetchUnassignedTasksForPm = async (page: number, limit: number): Promise<ITask[]> => {
        try {
            const response = await axios.get(`${TASK_ENDPOINTS.GET_UNASSIGNED_TASKS_AS_PM}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
                params: {
                    page: page,
                    limit: limit
                }
            });

            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const updateTask = async (taskId: string, task: ITask): Promise<ITask> => {
        try {
            const response = await axios.put(`${TASK_ENDPOINTS.PUT_TASK}/${taskId}`,
                { task },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const updateStatus = async (taskId: string, status: ETaskStatus): Promise<ITask> => {
        try {
            const response = await axios.put(`${TASK_ENDPOINTS.PUT_TASK_STATUS}/${taskId}`,
                {
                    status: status
                }, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const shortlistFreelancerToggle = async (taskId: string, freelancerId: string): Promise<ITask> => {
        try {
            const response = await axios.post(`${TASK_ENDPOINTS.POST_SHORTLIST_FREELANCER_TOGGLE}/${taskId}/${freelancerId}`,
                {},
                { headers: { Authorization: `${authToken}` } });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const pushRank = async (taskId: string, rank: number): Promise<{
        pushedTask: ITask,
        allTasks: ITask[]
    }> => {
        try {
            const response = await axios.put(`${TASK_ENDPOINTS.PUT_PUSH_TASK_RANK}/${taskId}/${rank}`,
                {},
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const addCommentToTask = async (taskId: string, comment: string) => {
        try {
            const response = await axios.post
                (`${TASK_ENDPOINTS.POST_COMMENT_TO_TASK}/${taskId}`,
                    { comment },
                    {
                        headers: {
                            Authorization: `${authToken}`,
                        },
                    });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const toggleLikeComment = async (taskId: string, commentId: string) => {
        try {
            const response = await axios.put(
                `${TASK_ENDPOINTS.PUT_TOGGLE_LIKE_COMMENT}/${taskId}/${commentId}`,
                {},
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                })
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const deleteComment = async (taskId: string, commentId: string) => {
        try {
            const response = await axios.delete(`${TASK_ENDPOINTS.DELETE_COMMENT}/${taskId}/${commentId}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const addRevisionToTask = async (taskId: string, message: string): Promise<ITask> => {
        try {
            const response = await axios.post(
                `${TASK_ENDPOINTS.POST_REVISION_TO_TASK}/${taskId}`,
                { message },
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const deleteTask = async (taskId: string): Promise<{
        project: IProject,
        tasks: ITask[]
    }> => {
        try {
            const response = await axios.delete(`${TASK_ENDPOINTS.DELETE_TASK}/${taskId}`,

                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const unassignFreelancer = async (taskId: string): Promise<{
        freelancer: IFreelancer,
        task: ITask,
        project: IProject,
    }> => {
        try {
            const response = await axios.delete(`${TASK_ENDPOINTS.DELETE_UNASSIGN_FREELANCER}/${taskId}`, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            console.error(error)
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }


    const addFreelancerHours = async (taskId: string, hours: number): Promise<ITask> => {
        try {
            const response = await axios.
                put(`${TASK_ENDPOINTS.PUT_TASK_ACTUAL_EFFORT}/${taskId}/${hours}`, {}, {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });

            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const approveTask = async (taskId: string) => {
        try {
            const response = await axios.put(`${TASK_ENDPOINTS.PUT_TASK_APPROVE}/${taskId}`, {}, {
                headers: {
                    Authorization: `${authToken}`,
                },
            });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const startAndPayTask = async (taskId: string): Promise<{
        task: ITask,
        wallet: EmployerWallet,
    }> => {
        try {
            const response = await axios.put(`${TASK_ENDPOINTS.PUT_TASK_START_AND_PAY}/${taskId}`,
                {},
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const completeTask = async (taskId: string): Promise<{
        task: ITask,
        project: IProject,
    }> => {
        try {
            const response = await axios.put(`${TASK_ENDPOINTS.PUT_TASK_COMPLETE}/${taskId}`,
                {},
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            console.log(response.data);
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    const cancelTask = async (taskId: string) => {
        try {
            const response = await axios.put(`${TASK_ENDPOINTS.PUT_TASK_CANCEL}/${taskId}`,
                {},
                {
                    headers: {
                        Authorization: `${authToken}`,
                    },
                });
            return response.data;
        } catch (error: any) {
            // Check if the error has a response and a data property
            if (error.response && error.response.data && error.response.data.error) {
                throw new Error(error.response.data.error); // Access the error message
            } else {
                throw new Error('An unexpected error occurred');
            }
        }
    }

    return {
        createTask,
        generateTasksAi,
        fetchTaskById,
        fetchTasksByIds,
        fetchTasksByFilter,
        fetchTasksForPmByFilter,
        fetchTasksByProjectId,
        fetchAllAvailableTasksAsFreelancer,
        fetchAvailableTasksAsFreelancerByFilter,
        fetchAllUnassignedTasks,
        fetchUnassignedTasksForPm,
        updateTask,
        updateStatus,
        shortlistFreelancerToggle,
        pushRank,
        addCommentToTask,
        toggleLikeComment,
        deleteTask,
        deleteComment,
        addRevisionToTask,
        unassignFreelancer,
        // setEstimatedEffort,
        addFreelancerHours,
        approveTask,
        startAndPayTask,
        completeTask,
        cancelTask,
    };
}

export default useTasks;